
import React from "react";
import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";

// Import custom hook and components
import { useStaffManagement } from "@/hooks/useStaffManagement";
import StaffProgress from "./staff/StaffProgress";
import NavigationButtons from "./staff/NavigationButtons";
import StageRenderer from "./staff/StageRenderer";
import { logger } from "@/lib/logger";
import { useUserBusiness } from "@/hooks/staff/useUserBusiness";
import { staffApiClient } from "@/api/staff";
import { useStaffConverter } from "@/hooks/staff/useStaffConverter";
import { AIStaffCandidate } from "@/lib/types";

interface StaffManagementProps {
  specificStaffId?: string;
}

export const StaffManagement: React.FC<StaffManagementProps> = ({ specificStaffId }) => {
  // Validate staffId - check if it's a valid UUID
  const isValidStaffId = specificStaffId && 
    specificStaffId !== ':staffId' && 
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(specificStaffId);
  
  // If specificStaffId is provided but invalid, log a warning
  if (specificStaffId && !isValidStaffId) {
    logger.warn({
      message: "Invalid staffId format passed to StaffManagement component",
      context: { specificStaffId }
    });
  }

  // Only pass valid staff IDs to the hook
  const validStaffId = isValidStaffId ? specificStaffId : undefined;

  const { convertStaffData } = useStaffConverter();
  const { fetchCurrentUser, fetchBusinessId } = useUserBusiness();

  // Get current user
  const { data: userId } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
    staleTime: 300000, // 5 minutes
  });
  
  // Get business ID
  const { data: businessId } = useQuery({
    queryKey: ['businessId', userId],
    queryFn: () => fetchBusinessId(userId!),
    enabled: !!userId,
    staleTime: 300000, // 5 minutes
  });

  // Fetch staff data using React Query
  const { 
    data: aiStaff,
    isLoading: isStaffLoading 
  } = useQuery({
    queryKey: ['staffManagement', businessId, validStaffId],
    queryFn: async () => {
      if (!businessId) return null;
      
      if (validStaffId) {
        const data = await staffApiClient.fetchStaffById(businessId, validStaffId);
        return data ? convertStaffData(data) : null;
      }
      return null;
    },
    enabled: !!businessId && !!validStaffId,
    staleTime: 60000, // 1 minute
  });

  // Use the remaining staff management hooks and state
  const {
    currentStage,
    progress,
    isSubmitting,
    selectedCandidate,
    staffStages,
    handleNextStage,
    handlePreviousStage,
    handleSelectCandidate,
    handleSelectPlan,
    handleComplete,
    handleCancelSubscription,
    isViewingExistingStaff,
    staffId,
  } = useStaffManagement(validStaffId);

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 animate-fade-in">
      {!isViewingExistingStaff && (
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-2">AI Staff Hiring</h2>
          <p className="text-muted-foreground">
            Choose and configure your new AI staff member
          </p>
        </div>
      )}
      
      {!isViewingExistingStaff && (
        <StaffProgress 
          staffStages={staffStages}
          currentStage={currentStage}
          progress={progress}
        />
      )}
      
      <Card className="glass-card p-6 transition-all duration-300">
        <StageRenderer
          currentStage={isViewingExistingStaff ? 4 : currentStage}
          selectedCandidate={selectedCandidate}
          isSubmitting={isSubmitting}
          aiStaff={aiStaff}
          staffId={staffId || validStaffId}
          onSelectCandidate={handleSelectCandidate}
          onSelectPlan={handleSelectPlan}
          onComplete={handleComplete}
          onCancelSubscription={handleCancelSubscription}
          isLoading={isStaffLoading}
        />
        
        {!isViewingExistingStaff && (
          <NavigationButtons 
            currentStage={currentStage}
            totalStages={staffStages.length}
            onPrevious={handlePreviousStage}
            onNext={handleNextStage}
          />
        )}
      </Card>
    </div>
  );
};

export default StaffManagement;
